import { PermissionKey } from '../../../utils/apollo/graphqlSchema';
import { appRoutes } from '../../../path';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import toastMessage from '../../../utils/toastMessage';

export type PageNameWithSubPermissions = 'dictionary' | 'cohort' | 'schedule';
/**
 * This is the sequence of dictionary items in the sidebar.
 * Once user entering a dictionary page without permission,
 * then redirect user to the first available dictionary page.
 */

const pageConfig: Record<
  PageNameWithSubPermissions,
  {
    permissionKeys: PermissionKey[];
    path: string;
  }[]
> = {
  dictionary: [
    {
      permissionKeys: [PermissionKey.MeasureRead],
      path: appRoutes.dictionary.measure,
    },
    {
      permissionKeys: [PermissionKey.EventRead],
      path: appRoutes.dictionary.event,
    },
    {
      permissionKeys: [PermissionKey.VirtualEventRead],
      path: appRoutes.dictionary.virtualEvent,
    },
    {
      permissionKeys: [PermissionKey.FieldRead],
      path: appRoutes.dictionary.field,
    },
    {
      permissionKeys: [PermissionKey.FilterRead],
      path: appRoutes.dictionary.filter,
    },
    {
      permissionKeys: [PermissionKey.BucketDefRead],
      path: appRoutes.dictionary.buckets,
    },
  ],
  cohort: [
    {
      permissionKeys: [PermissionKey.CohortReportRead],
      path: appRoutes.cohorts.create,
    },
    {
      permissionKeys: [PermissionKey.CohortRead],
      path: appRoutes.cohorts.view,
    },
  ],
  schedule: [
    {
      permissionKeys: [PermissionKey.DashboardScheduleRead],
      path: appRoutes.schedules.dashboard,
    },
    {
      permissionKeys: [PermissionKey.AnalysisScheduleRead],
      path: appRoutes.schedules.report,
    },
  ],
};

interface IProps {
  pageNameWithSubPermissions: PageNameWithSubPermissions | undefined;
  isPermittedToViewPage: boolean;
}
/**
 * Check if need to redirect to next available page, it needs to satisfy two conditions:
 * 1. no permissions to view current page,
 * 2. the current page is a subpage which belongs to diff permissions.
 *
 * if found the next available page, return the path, else return undefined.
 */
const useRedirectNextAvailablePage = ({
  pageNameWithSubPermissions,
  isPermittedToViewPage,
}: IProps) => {
  const { checkIsPermitted } = usePermission();

  if (isPermittedToViewPage || !pageNameWithSubPermissions) {
    return;
  }

  const config = pageConfig[pageNameWithSubPermissions];

  for (const page of config) {
    if (
      checkIsPermitted({
        requiredPermissionKeys: page.permissionKeys,
      })
    ) {
      toastMessage({
        message: `Access to the page is denied due to insufficient permissions.`,
        type: 'error',
      });
      return page.path;
    }
  }
};

export default useRedirectNextAvailablePage;
